import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import PageAlert from "../../components/PageAlert/PageAlert";
import DataTable, { DataTableActions, DataTableFooter } from "../../components/DataTable";
import { fetchEnquiries } from "../../api/enquiryApi";
import { resolveApiError } from "../../utils/apiError";
import ModuleSearchControls, {
  ModuleFilterMenu,
  ModulePerPageMenu,
  ModuleRadioOption,
  ModuleSortMenu,
} from "../../components/ModuleSearchControls";
import "./enquiries.scss";

const labels = { new: "New", in_progress: "In progress", closed: "Closed" };

const SORT_OPTIONS = [
  { value: "created_at", label: "Submitted date" },
  { value: "enquiry_number", label: "Enquiry number" },
  { value: "full_name", label: "Contact name" },
  { value: "company", label: "Company" },
  { value: "status", label: "Status" },
];

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const EnquiriesPage = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [summary, setSummary] = useState({ total: 0, new: 0, in_progress: 0, closed: 0 });
  const [meta, setMeta] = useState(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [perPageOpen, setPerPageOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchEnquiries({
        search: debouncedQuery || undefined,
        status: status === "all" ? undefined : status,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        per_page: perPage,
      });
      setEnquiries(data.enquiries || []);
      setMeta(data.meta);
      setSummary(data.summary || { total: 0, new: 0, in_progress: 0, closed: 0 });
    } catch (e) {
      resolveApiError(e, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, status, sortBy, sortOrder, page, perPage]);

  useEffect(() => {
    loadEnquiries();
  }, [loadEnquiries]);

  const resetFilters = () => {
    setQuery("");
    setDebouncedQuery("");
    setStatus("all");
    setSortBy("created_at");
    setSortOrder("desc");
    setPerPage(20);
    setPage(1);
  };

  return (
    <div className="enquiries-page">
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
      <section className="enquiries-page__toolbar">
        <div>
          <h2>Product enquiries</h2>
          <p>Review customer requests for one or more catalogue or custom products.</p>
        </div>
      </section>
      <section className="enquiries-page__summary">
        {[
          [summary.total, "Total enquiries"],
          [summary.new, "New"],
          [summary.in_progress, "In progress"],
          [summary.closed, "Closed"],
        ].map(([value, label]) => (
          <div key={label}>
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </section>
      <section className="enquiries-page__card">
        <ModuleSearchControls value={query} onChange={setQuery} placeholder="Search name, company or product">
          <ModuleFilterMenu
            open={filterOpen}
            onToggle={() => {
              setFilterOpen((open) => !open);
              setSortOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setFilterOpen(false)}
            count={status === "all" ? 0 : 1}
            columns={1}
            size="narrow"
            ariaLabel="Enquiry filters"
          >
            <fieldset>
              <legend>Status</legend>
              {[
                ["all", "All statuses"],
                ["new", "New"],
                ["in_progress", "In progress"],
                ["closed", "Closed"],
              ].map(([value, label]) => (
                <ModuleRadioOption
                  key={value}
                  name="enquiry-status"
                  checked={status === value}
                  onChange={() => {
                    setStatus(value);
                    setPage(1);
                  }}
                  plain
                >
                  {label}
                </ModuleRadioOption>
              ))}
            </fieldset>
          </ModuleFilterMenu>
          <ModuleSortMenu
            open={sortOpen}
            onToggle={() => {
              setSortOpen((open) => !open);
              setFilterOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setSortOpen(false)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            options={SORT_OPTIONS}
            onChangeSortBy={(value) => {
              setSortBy(value);
              setPage(1);
            }}
            onChangeSortOrder={(value) => {
              setSortOrder(value);
              setPage(1);
            }}
          />
          <ModulePerPageMenu
            open={perPageOpen}
            onToggle={() => {
              setPerPageOpen((open) => !open);
              setFilterOpen(false);
              setSortOpen(false);
            }}
            onClose={() => setPerPageOpen(false)}
            value={perPage}
            onChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
          <button className="ui-button" type="button" onClick={resetFilters}>
            <Icon icon="ph:arrow-clockwise" height={14} /> Reset
          </button>
        </ModuleSearchControls>
        <DataTable
          minWidth={980}
          loading={loading}
          loadingMessage="Loading enquiries…"
          emptyMessage="No enquiries match your filters."
          rows={enquiries}
          columns={[
            {
              key: "enquiry",
              header: "Enquiry",
              render: (enquiry) => {
                const items = enquiry.items || [];
                return (
                  <>
                    <strong>{enquiry.enquiry_number}</strong>
                    <small>
                      {items.length} product line{items.length === 1 ? "" : "s"}
                    </small>
                  </>
                );
              },
            },
            {
              key: "contact",
              header: "Contact",
              render: (enquiry) => (
                <>
                  <strong>{enquiry.full_name}</strong>
                  <small>{enquiry.company || "—"}</small>
                </>
              ),
            },
            {
              key: "products",
              header: "Products",
              render: (enquiry) => (
                <div className="enquiries-page__products">
                  {(enquiry.items || []).slice(0, 2).map((item) => (
                    <span key={item.id || item.product_name}>{item.product_name}</span>
                  ))}
                </div>
              ),
            },
            { key: "delivery", header: "Delivery", render: (enquiry) => enquiry.delivery_location || "—" },
            {
              key: "status",
              header: "Status",
              render: (enquiry) => (
                <span className={`enquiries-page__status enquiries-page__status--${enquiry.status}`}>
                  {labels[enquiry.status] || enquiry.status}
                </span>
              ),
            },
            {
              key: "submitted",
              header: "Submitted",
              render: (enquiry) => formatDate(enquiry.submitted_at || enquiry.created_at),
            },
            {
              key: "actions",
              header: "Action",
              render: (enquiry) => (
                <DataTableActions>
                  <button type="button" onClick={() => navigate(`/admin/enquiries/${enquiry.id}`)}>
                    View
                  </button>
                </DataTableActions>
              ),
            },
          ]}
        />
        <DataTableFooter meta={meta} page={page} loading={loading} onPageChange={setPage} noun="enquiries" />
      </section>
    </div>
  );
};

export default EnquiriesPage;
